import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Affiliate, Case, Member, Organization, Query, User, Workspace } from '@telespot/sdk';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService, DataService } from '../../services';

@Injectable({
  providedIn: 'root',
})
export class CaseAccessGuard  {
  constructor(private _authService: AuthService, private _dataService: DataService, private _router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const user = this._authService.currentUser;
    const caseId = route.params['caseid'];
    const allowedRoles = route.data.allowedRoles;

    if (!caseId) {
      return from([this._router.createUrlTree(['/page-not-found'])]);
    }

    const caseQuery = new Query(Case).include(['organization', 'workspace']);

    return from(this._dataService.get(caseId, caseQuery)).pipe(
      switchMap((_case) => {
        if (!_case) return from([this._router.createUrlTree(['/page-not-found'])]);

        const { organization, workspace } = _case;
        const checks: Observable<boolean>[] = [];

        if (!organization || !workspace) return from([this._router.createUrlTree(['/page-not-found'])]);

        if (this._checkRoleAccess(user, allowedRoles)) return of(true);

        checks.push(this._checkMembershipInOrganization(organization, user));
        checks.push(this._checkWorkspaceMembership(workspace, user));

        return combineLatest(checks).pipe(
          map((results) => results.every((hasAccess) => hasAccess)),
          map((hasAccess) => hasAccess || this._router.createUrlTree(['/page-not-authorized']))
        );
      }),
      // Fetching a case the user can't access (scoped out by the parse-server
      // beforeFind hooks / ACL) rejects with "object not found". Without this
      // the guard's observable would error, the navigation would be cancelled
      // and the user left on a blank screen. Treat it as unauthorized instead.
      catchError(() => of(this._router.createUrlTree(['/page-not-authorized'])))
    );
  }

  private _checkMembershipInOrganization(organization: Organization, user: User): Observable<boolean> {
    return from(
      this._dataService.first(
        new Query(Affiliate).equalTo('user', User.createWithoutData(user.id)).equalTo('organization', organization)
      )
    ).pipe(map((affiliate) => !!affiliate));
  }

  private _checkWorkspaceMembership(workspace: Workspace, user: User): Observable<boolean> {
    return from(
      this._dataService.first(
        new Query(Member).equalTo('user', User.createWithoutData(user.id)).equalTo('workspace', workspace)
      )
    ).pipe(map((member) => !!member));
  }

  private _checkRoleAccess(user: User, allowedRoles?: string[]): boolean {
    return allowedRoles?.length ? user?.is(allowedRoles) : true;
  }
}
